import React, { useState } from 'react';
import { Star, MessageSquare } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useReviews, ReviewSubmission } from '@/hooks/useReviews';

interface ReviewSubmissionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  revieweeUserId: string;
  reviewerType: 'client' | 'vendor';
  organizationId: string;
  onSuccess?: () => void;
}

const StarRating: React.FC<{
  value: number;
  onChange: (value: number) => void;
  label: string;
  required?: boolean;
}> = ({ value, onChange, label, required = false }) => {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">
        {label} {required && <span className="text-destructive">*</span>}
      </Label>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            className="p-1 hover:scale-110 transition-transform"
          >
            <Star
              className={`h-6 w-6 ${
                star <= value
                  ? 'fill-yellow-400 text-yellow-400'
                  : 'text-muted-foreground hover:text-yellow-400'
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

export const ReviewSubmissionModal: React.FC<ReviewSubmissionModalProps> = ({
  open,
  onOpenChange,
  jobId,
  revieweeUserId,
  reviewerType,
  organizationId,
  onSuccess
}) => {
  const [overallRating, setOverallRating] = useState(0);
  const [qualityRating, setQualityRating] = useState(0);
  const [timelinessRating, setTimelinessRating] = useState(0);
  const [communicationRating, setCommunicationRating] = useState(0);
  const [writtenReview, setWrittenReview] = useState('');
  
  const { submitReview, submitting } = useReviews();

  const handleSubmit = async () => {
    if (overallRating === 0) return;

    const reviewData: ReviewSubmission = {
      overall_rating: overallRating,
      written_review: writtenReview.trim() || undefined
    };

    if (qualityRating > 0) reviewData.quality_rating = qualityRating;
    if (timelinessRating > 0) reviewData.timeliness_rating = timelinessRating;
    if (communicationRating > 0) reviewData.communication_rating = communicationRating;

    const success = await submitReview(
      jobId,
      revieweeUserId,
      reviewerType,
      organizationId,
      reviewData
    );

    if (success) {
      resetForm();
      onOpenChange(false);
      onSuccess?.();
    }
  };

  const handleSkip = () => {
    resetForm();
    onOpenChange(false);
  };

  const resetForm = () => {
    setOverallRating(0);
    setQualityRating(0);
    setTimelinessRating(0);
    setCommunicationRating(0);
    setWrittenReview('');
  };

  const getQualityQuestion = () => {
    return reviewerType === 'client' 
      ? "How would you rate the quality of work delivered?"
      : "How would you rate the quality of specifications, requirements and assets provided?";
  };

  const getTimelinessQuestion = () => {
    return reviewerType === 'client'
      ? "Did the vendor meet deadlines and complete work on time?"
      : "Did the client provide timely assets, approvals and feedback?";
  };

  const getCommunicationQuestion = () => {
    return reviewerType === 'client'
      ? "Was the vendor responsive and clear in communications?"
      : "Was the client responsive and clear in communications?";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-primary" />
            Leave a Review
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="text-sm text-muted-foreground">
            Your review will be published when both parties complete their reviews, 
            or after 14 days if only one party submits.
          </div>

          <StarRating
            value={overallRating}
            onChange={setOverallRating}
            label="Overall Rating"
            required
          />

          <div className="space-y-4 border-t pt-4">
            <h4 className="font-medium text-sm">Optional Detailed Ratings</h4>
            
            <StarRating
              value={qualityRating}
              onChange={setQualityRating}
              label={`Quality - ${getQualityQuestion()}`}
            />

            <StarRating
              value={timelinessRating}
              onChange={setTimelinessRating}
              label={`Timeliness - ${getTimelinessQuestion()}`}
            />

            <StarRating
              value={communicationRating}
              onChange={setCommunicationRating}
              label={`Communication - ${getCommunicationQuestion()}`}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="written-review" className="text-sm font-medium">
              Written Review (Optional)
            </Label>
            <Textarea
              id="written-review"
              placeholder="Share your experience working together..."
              value={writtenReview}
              onChange={(e) => setWrittenReview(e.target.value.slice(0, 500))}
              rows={4}
            />
            <div className="text-xs text-muted-foreground text-right">
              {writtenReview.length}/500 characters
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={handleSkip}
            disabled={submitting}
          >
            Skip for Now
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={overallRating === 0 || submitting}
          >
            {submitting ? "Submitting..." : "Submit Review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};